import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { signIn } from '@/lib/auth-client';
import { Turnstile } from '@/components/Turnstile';
import { authErrorMessage } from '@/lib/auth-error';
import { AuthLayout, AuthDivider, AuthTextField, GoogleIcon } from '@/components/AuthLayout';
import { Button } from '@/components/ui';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Same two-store pattern as Signup so a fast click between captcha
  // solving and React re-rendering still ships the freshest token.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onCaptchaToken(token: string) {
    captchaRef.current = token;
    setCaptchaToken(token);
  }
  function onCaptchaExpired() {
    captchaRef.current = null;
    setCaptchaToken(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const token = captchaRef.current;
    if (!token) {
      setErr('Please complete the captcha below first.');
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await signIn.email({
      email,
      password,
      fetchOptions: { headers: { 'x-captcha-response': token } },
    });
    setBusy(false);
    if (error) {
      setErr(authErrorMessage(error, 'Invalid email or password.'));
      captchaRef.current = null;
      setCaptchaToken(null);
      return;
    }
    navigate('/app');
  }

  async function onGoogle() {
    setErr(null);
    const { error } = await signIn.social({ provider: 'google', callbackURL: '/app' });
    if (error) setErr(authErrorMessage(error, 'Google sign-in failed.'));
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to manage your bio links and analytics."
      topRight={
        <Link
          to="/signup"
          className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
        >
          Need an account?
        </Link>
      }
    >
      <Button variant="ghost" fullWidth onClick={onGoogle} leadingIcon={<GoogleIcon />}>
        Continue with Google
      </Button>

      <AuthDivider />

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthTextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
        <AuthTextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />

        <Turnstile onToken={onCaptchaToken} onExpired={onCaptchaExpired} onError={onCaptchaExpired} />

        {err && <p className="text-sm text-[var(--color-destructive)]">{err}</p>}

        <Button
          type="submit"
          fullWidth
          size="lg"
          disabled={busy || !captchaToken}
          title={!captchaToken ? 'Complete the captcha above first' : undefined}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-center text-xs text-[var(--color-on-surface-variant)]">
          By signing in you agree to our{' '}
          <Link to="/terms" className="underline">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
      </form>
    </AuthLayout>
  );
}
